import { Notebook, MindMapNode, AIBehaviorSettings } from "../types";
import { db, storage, functions } from "./firebase";
import { collection, doc, setDoc, getDocs, writeBatch, query, getDoc, updateDoc, onSnapshot, orderBy, limit, getDocs as getDocsAlias, deleteField, deleteDoc, runTransaction, where, serverTimestamp, Timestamp } from "firebase/firestore";
import { mergeNoteText, MAX_CONFLICT_COPIES, type NoteText, type TextConflictCopy } from "../utils/textMerge";
import { mergeAnnotations, sameAnnotations, type Annotations } from "../utils/annotationMerge";
import { ref, uploadString, getDownloadURL, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

// --- IMAGE & PDF HANDLING ---

// Helper: Calculate string size in bytes (UTF-8)
const getStringByteSize = (str: string): number => {
    return new TextEncoder().encode(str).length;
};

// --- DEBUG LOGGER ---
type LogListener = (msg: string) => void;
let debugListeners: LogListener[] = [];
export const onDebugLog = (listener: LogListener) => {
    debugListeners.push(listener);
    return () => { debugListeners = debugListeners.filter(l => l !== listener); };
};
const logToOverlay = (msg: string) => {
    // console.log(`[OVERLAY] ${msg}`);
    const timestamped = `[${new Date().toLocaleTimeString().split(' ')[0]}] ${msg}`;
    debugListeners.forEach(l => l(timestamped));
};

// Upload validation constants
const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_BASE64_UPLOAD_LENGTH = 13_333_333; // ~10MB in base64

// Upload a Base64 image string to Firebase Storage and return the URL
const uploadImageToStorage = async (userId: string, base64: string, mimeType: string): Promise<string | null> => {
    if (base64.startsWith('http')) return base64;

    // Validate MIME type
    if (!ALLOWED_IMAGE_MIME_TYPES.includes(mimeType)) {
        console.error(`Upload rejected: invalid MIME type "${mimeType}"`);
        return null;
    }

    // Validate size (~10MB max)
    if (base64.length > MAX_BASE64_UPLOAD_LENGTH) {
        console.error(`Upload rejected: file too large (${(base64.length / 1_000_000).toFixed(1)}MB)`);
        return null;
    }

    try {
        const id = Math.random().toString(36).substring(2, 15);
        const ext = mimeType.split('/')[1] || 'png';
        const path = `user_content/${userId}/images/${id}.${ext}`;
        const storageRef = ref(storage, path);

        await uploadString(storageRef, base64, 'data_url');
        const url = await getDownloadURL(storageRef);
        return url;
    } catch (error) {
        console.error("Image upload failed", error);
        return null;
    }
};

// Upload a PDF Blob URL to Firebase Storage and return the permanent URL
const uploadPdfToStorage = async (userId: string, blobUrl: string, notebookId: string): Promise<string | null> => {
    try {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        const path = `user_content/${userId}/pdfs/notebook_${notebookId}.pdf`;
        const storageRef = ref(storage, path);

        await uploadBytes(storageRef, blob, { contentType: 'application/pdf' });
        const url = await getDownloadURL(storageRef);
        return `${url}&t=${Date.now()}`;
    } catch (error) {
        console.error("PDF upload failed", error);
        return null;
    }
};

const processNotebookForSave = async (userId: string, notebook: Notebook): Promise<Notebook> => {
    let pdfContextUrl = notebook.pdfContextUrl;
    if (pdfContextUrl && pdfContextUrl.startsWith('blob:')) {
        const url = await uploadPdfToStorage(userId, pdfContextUrl, notebook.id);
        // Never persist a blob: URL — it's only valid in the tab that created it.
        // If upload fails, drop the reference entirely rather than saving a permanently-dead link.
        pdfContextUrl = url ?? undefined;
        if (!url) console.error(`PDF upload failed for notebook ${notebook.id} — clearing pdfContextUrl to avoid persisting a dead blob: URL`);
    }

    const processedNodes = await Promise.all(notebook.nodes.map(async (node) => {
        let imageBase64 = node.imageBase64;
        let drawingBase64 = node.drawingBase64;
        let attachments = node.attachments;
        let hasChanges = false;

        if (imageBase64 && imageBase64.startsWith('data:')) {
            const url = await uploadImageToStorage(userId, imageBase64, node.imageMimeType || 'image/png');
            if (url) {
                imageBase64 = url;
                hasChanges = true;
            }
        }

        if (drawingBase64 && drawingBase64.startsWith('data:')) {
            const url = await uploadImageToStorage(userId, drawingBase64, 'image/png');
            if (url) {
                drawingBase64 = url;
                hasChanges = true;
            }
        }

        if (attachments && attachments.length > 0) {
            const newAttachments = await Promise.all(attachments.map(async (att) => {
                if (att.content && att.content.startsWith('data:')) {
                    const url = await uploadImageToStorage(userId, att.content, att.mimeType);
                    if (url) return { ...att, content: url };
                }
                return att;
            }));

            if (JSON.stringify(newAttachments) !== JSON.stringify(attachments)) {
                attachments = newAttachments;
                hasChanges = true;
            }
        }

        if (!hasChanges) return node;

        return {
            ...node,
            imageBase64,
            drawingBase64,
            attachments
        };
    }));

    return { ...notebook, nodes: processedNodes, pdfContextUrl };
};

/**
 * Clean up transient/temporary fields that don't need to be saved
 * This reduces notebook size by removing UI state that gets recreated on load
 */
const cleanupTransientFields = (notebook: Notebook): Notebook => {
    return {
        ...notebook,
        nodes: notebook.nodes.map(node => {
            // Create a copy and remove transient fields
            const {
                isAnalyzing,
                isGeneratingQuestions,
                isGenerating,
                ...cleanNode
            } = node as any;

            // Never persist an in-progress status — if the tab closes or the
            // active notebook changes while generateReviewQuestions is running,
            // the .then() callback can't fire and the node stays stuck at
            // 'generating' forever. Reset to 'none' so the spinner clears on reload.
            if (cleanNode.reviewQuestionStatus === 'generating') {
                cleanNode.reviewQuestionStatus = 'none';
            }

            // Limit review history to last 10 entries (most recent)
            if (cleanNode.reviewSessions && cleanNode.reviewSessions.length > 10) {
                cleanNode.reviewSessions = cleanNode.reviewSessions.slice(-10);
            }

            return cleanNode;
        })
    };
};

// ── Field-level save baseline ────────────────────────────────────────────────
// Node docs are also written by things other than this app's autosave — YouTube Manager (titles,
// descriptions, transcripts, AI Check results…), the upload flow, Cloud Functions (cached
// translations, video-worthiness scores). Autosave used to `set()` the WHOLE doc from this tab's
// in-memory copy, which was loaded when the app opened — so the next save of that note silently
// reverted every one of those external writes. Now we remember a signature of each field as last
// seen on the server (on load, and after each of our own saves) and only write fields this tab
// actually changed since then; untouched fields keep whatever the server has.
const serverFieldSigs = new Map<string, Map<string, number>>(); // "uid/nb/node" → field → signature
const nodePath = (userId: string, notebookId: string, nodeId: string) => `${userId}/${notebookId}/${nodeId}`;

// Key-order-independent JSON, so a map that round-tripped through Firestore (which may reorder
// keys) still matches the in-memory value it came from.
const stableStringify = (v: any): string => {
    if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
    if (Array.isArray(v)) return '[' + v.map(x => x === undefined ? 'null' : stableStringify(x)).join(',') + ']';
    if (typeof (v as any).toMillis === 'function') return `ts:${(v as any).toMillis()}`; // Firestore Timestamp
    const keys = Object.keys(v).filter(k => v[k] !== undefined).sort();
    return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(v[k])).join(',') + '}';
};
// 53-bit hash of the field's JSON — keeps the baseline small even for long note bodies.
const fieldSig = (v: any): number => {
    const s = stableStringify(v);
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < s.length; i++) {
        const ch = s.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};
// Fields of a note that belong to the video tools (YouTube Manager, the upload flow). Those write
// them straight to the server, one video at a time. Autosave must never send them from this tab's
// in-memory copy: that copy is as old as the tab, and writing it back reverted titles,
// descriptions and AI checks made in the Manager, and could drop a video from the note.
// (videoUrl / videoTitle / videoDuration stay out of this list: the note's own "edit video link"
// dialog saves them through autosave.)
export const VIDEO_OWNED_FIELDS: ReadonlySet<string> = new Set([
    'youtubeVideoId', 'youtubeVideos', 'youtubeStatus', 'youtubeTitle', 'youtubeDescription',
    'youtubeUploadedAt', 'youtubeScheduledAt', 'youtubeEditedAt', 'youtubeTranscript', 'youtubeTimedTranscript',
    'videoSegments', 'videoClipFocus', 'videoStoragePath', 'showOnGithub',
    'postCheckScore', 'postCheckRecommendation', 'postCheckStrengths', 'postCheckPasses', 'postCheckFindings',
    'postCheckSummary', 'postCheckCheckedAt',
    'thumbnailSettings', 'thumbnailPushedAt', 'thumbnailDrafts', 'thumbnailStudio', 'exportJobId', 'clipOfJobId',
]);

const recordServerFields = (path: string, data: Record<string, any>) => {
    const m = new Map<string, number>();
    for (const [k, v] of Object.entries(data)) if (v !== undefined) m.set(k, fieldSig(v));
    serverFieldSigs.set(path, m);
    recordServerText(path, data);
};

// ── Note text sync (title + content) ───────────────────────────────────────────────────────
// The note's title and body are the one part two devices (web, phone) edit by hand. Every write
// of them is a compare-and-set inside a transaction: it only goes through if the server still
// has the text (and revision) this tab started from. If someone else saved in between, the two
// edits are merged three-way (utils/textMerge); if they touch the same words, this tab's text
// wins and the other version is kept on the note in `textConflicts`, so nothing is ever lost.
// textRev counts text writes; textUpdatedAt is a server timestamp so other devices can listen
// for "notes whose text changed since I loaded" without re-reading the whole notebook.
export const TEXT_FIELDS = ['title', 'content'] as const;
const TEXT_META_FIELDS = ['textRev', 'textUpdatedAt', 'textUpdatedBy', 'textWriterId', 'textConflicts', 'updatedAt'];
/** Identifies this browser tab, so its own writes coming back through the listener are ignored. */
export const TAB_WRITER_ID = `web-${Math.random().toString(36).slice(2, 10)}`;
interface ServerText extends NoteText { rev: number }
const serverText = new Map<string, ServerText>(); // "uid/nb/node" → text as last seen on the server
function recordServerText(path: string, data: Record<string, any>) {
    serverText.set(path, { title: data.title ?? '', content: data.content ?? '', rev: Number(data.textRev ?? 0) });
}
const setFieldSigs = (path: string, data: Record<string, any>) => {
    const m = serverFieldSigs.get(path) ?? new Map<string, number>();
    for (const [k, v] of Object.entries(data)) {
        if (v === undefined) m.delete(k);
        else if (typeof (v as any)?.isEqual === 'function' && !(v as any).toMillis) continue; // FieldValue sentinel — sig set when read back
        else m.set(k, fieldSig(v));
    }
    serverFieldSigs.set(path, m);
};

export type TextSaveOutcome = 'saved' | 'merged' | 'conflict';
export interface TextSaveResult {
    userId: string; notebookId: string; nodeId: string;
    outcome: TextSaveOutcome;
    /** The text this tab sent. */
    ours: NoteText;
    /** What the server holds now. */
    result: NoteText;
    textRev: number;
    textConflicts?: TextConflictCopy[];
}
type TextSaveListener = (r: TextSaveResult) => void;
const textSaveListeners = new Set<TextSaveListener>();
/** Called after a text save that had to merge or resolve a conflict, so the UI can adopt the result. */
export const onTextSaveResult = (fn: TextSaveListener) => { textSaveListeners.add(fn); return () => { textSaveListeners.delete(fn); }; };

/** Compare-and-set write of a note's title/content. Exported for tests. */
export const saveNoteTextCAS = async (userId: string, notebookId: string, nodeId: string, ours: NoteText, extra: Record<string, any> = {}): Promise<TextSaveResult> => {
    const path = nodePath(userId, notebookId, nodeId);
    const ref = doc(db, "users", userId, "notebooks", notebookId, "nodes", nodeId);
    const res = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const d: Record<string, any> = snap.exists() ? snap.data() : {};
        const server: ServerText = { title: d.title ?? '', content: d.content ?? '', rev: Number(d.textRev ?? 0) };
        const base = serverText.get(path) ?? server;
        const unchanged = server.rev === base.rev && server.title === base.title && server.content === base.content;
        let outcome: TextSaveOutcome = 'saved';
        let result: NoteText = ours;
        let conflicts: TextConflictCopy[] | undefined;
        if (!unchanged) {
            const m = mergeNoteText(base, ours, server);
            if (m.clean) { outcome = 'merged'; result = m.merged; }
            else {
                outcome = 'conflict';
                const from = d.textUpdatedBy === 'mobile' ? 'mobile' : d.textUpdatedBy === 'web' ? 'web' : 'unknown';
                conflicts = [...(Array.isArray(d.textConflicts) ? d.textConflicts : []),
                    { title: server.title, content: server.content, from, savedAt: Date.now(), rev: server.rev }].slice(-MAX_CONFLICT_COPIES);
            }
        }
        const rev = Math.max(server.rev, base.rev) + 1;
        const write: Record<string, any> = {
            ...extra,
            title: result.title, content: result.content, textRev: rev,
            textUpdatedAt: serverTimestamp(), textUpdatedBy: 'web', textWriterId: TAB_WRITER_ID,
        };
        if (conflicts) write.textConflicts = conflicts;
        tx.set(ref, write, { merge: true });
        return { outcome, result, rev, conflicts, write };
    });
    serverText.set(path, { ...res.result, rev: res.rev });
    setFieldSigs(path, res.write);
    const out: TextSaveResult = { userId, notebookId, nodeId, outcome: res.outcome, ours, result: res.result, textRev: res.rev, textConflicts: res.conflicts };
    if (res.outcome !== 'saved') textSaveListeners.forEach(fn => { try { fn(out); } catch (e) { console.error(e); } });
    return out;
};

/** True when this tab's copy of the note's text is exactly what the server had last time we looked. */
export const isNoteTextAtBaseline = (userId: string, notebookId: string, nodeId: string, t: NoteText): boolean => {
    const b = serverText.get(nodePath(userId, notebookId, nodeId));
    return !!b && b.title === (t.title ?? '') && b.content === (t.content ?? '');
};

export interface RemoteTextChange {
    nodeId: string; title: string; content: string; textRev: number;
    data: Record<string, any>; // the text fields + metadata to copy into the local node
}
/**
 * Listens for notes in one notebook whose text another device changed after `sinceMs`.
 * Reads only the changed notes (query on textUpdatedAt), never the whole notebook.
 * Call acceptRemoteText() for each change the UI applies.
 */
export const subscribeToNoteTextChanges = (userId: string, notebookId: string, sinceMs: number, cb: (changes: RemoteTextChange[]) => void) => {
    const q = query(
        collection(db, "users", userId, "notebooks", notebookId, "nodes"),
        where('textUpdatedAt', '>', Timestamp.fromMillis(sinceMs - 120_000)), // 2 min slack for clock drift
    );
    return onSnapshot(q, (snap) => {
        const changes: RemoteTextChange[] = [];
        snap.docChanges().forEach(ch => {
            if (ch.type === 'removed' || ch.doc.metadata.hasPendingWrites) return;
            const d = ch.doc.data();
            if (d.textWriterId === TAB_WRITER_ID) return;
            const b = serverText.get(nodePath(userId, notebookId, ch.doc.id));
            const rev = Number(d.textRev ?? 0);
            if (b && rev <= b.rev && b.title === (d.title ?? '') && b.content === (d.content ?? '')) return; // already have it
            const data: Record<string, any> = { title: d.title ?? '', content: d.content ?? '' };
            for (const k of TEXT_META_FIELDS) if (d[k] !== undefined) data[k] = d[k];
            changes.push({ nodeId: ch.doc.id, title: data.title, content: data.content, textRev: rev, data });
        });
        if (changes.length) cb(changes);
    }, (err) => console.warn('[TEXT SYNC] listener error', err));
};

/** Replaces the note's kept conflict copies (dismiss one, or set the current text aside). */
export const setTextConflicts = async (userId: string, notebookId: string, nodeId: string, copies: TextConflictCopy[]) => {
    const ref = doc(db, "users", userId, "notebooks", notebookId, "nodes", nodeId);
    const value = copies.length ? copies.slice(-MAX_CONFLICT_COPIES) : deleteField();
    await updateDoc(ref, { textConflicts: value });
    setFieldSigs(nodePath(userId, notebookId, nodeId), { textConflicts: copies.length ? value : undefined });
};

/** Records that the local copy now holds the server's text for this note. */
export const acceptRemoteText = (userId: string, notebookId: string, change: RemoteTextChange) => {
    const path = nodePath(userId, notebookId, change.nodeId);
    serverText.set(path, { title: change.title, content: change.content, rev: change.textRev });
    setFieldSigs(path, change.data);
};

/**
 * Save nodes to Firestore subcollection (Phase 2: Subcollection Architecture)
 * This enables notebooks to exceed the 1MB document limit by storing each node separately
 */
const saveNodesToSubcollection = async (
    userId: string,
    notebookId: string,
    nodes: MindMapNode[],
    deletedNodeIds: string[] = [],
    // True sibling-order index for each node within the FULL notebook — required when `nodes`
    // is only a dirty subset (partial save), since that subset's own array position would
    // otherwise silently corrupt ordering. Falls back to array position when omitted, which is
    // only correct if `nodes` is the complete, correctly-ordered notebook array.
    nodeIndexById?: Map<string, number>
): Promise<void> => {
    const nodesCollection = collection(db, "users", userId, "notebooks", notebookId, "nodes");

    // Phase 1: DELETE explicitly-removed nodes only.
    // We used to infer deletions by diffing the full existing subcollection against whatever
    // node array this device happened to have locally — but a device with a stale/incomplete
    // local copy (e.g. it never received another device's edits) would then "clean up" nodes
    // it simply didn't know about, deleting perfectly live data. Callers now pass the exact
    // IDs that were actually deleted on this device; we never delete anything else.
    if (deletedNodeIds.length > 0) {
        console.log(`🧹 Deleting ${deletedNodeIds.length} explicitly-removed node(s)...`);
        const BATCH_SIZE = 500;
        for (let i = 0; i < deletedNodeIds.length; i += BATCH_SIZE) {
            const batch = writeBatch(db);
            const chunk = deletedNodeIds.slice(i, i + BATCH_SIZE);
            chunk.forEach(id => {
                batch.delete(doc(nodesCollection, id));
                serverFieldSigs.delete(nodePath(userId, notebookId, id));
            });
            await batch.commit();
        }
        console.log(`✅ Deleted ${deletedNodeIds.length} node(s) from subcollection`);
    }

    // Phase 2: SAVE CURRENT NODES
    // Helper: Clean undefined values (Firestore doesn't allow undefined) — recurses into
    // arrays too, since array elements (e.g. videoSegments[], youtubeVideos[]) can themselves
    // be objects with optional/undefined fields.
    const cleanNode = (value: any): any => {
        if (Array.isArray(value)) {
            return value.map(cleanNode);
        }
        if (value && typeof value === 'object') {
            const cleaned: any = {};
            for (const [key, v] of Object.entries(value)) {
                if (v !== undefined) cleaned[key] = cleanNode(v);
            }
            return cleaned;
        }
        return value;
    };

    // Firestore batch write limit is 500 operations
    // Split nodes into chunks if needed
    const BATCH_SIZE = 500;
    const chunks: MindMapNode[][] = [];

    for (let i = 0; i < nodes.length; i += BATCH_SIZE) {
        chunks.push(nodes.slice(i, i + BATCH_SIZE));
    }

    // Process each chunk
    let skipped = 0;
    const textJobs: { nodeId: string; ours: NoteText }[] = [];
    for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
        const batch = writeBatch(db);
        const committed: { path: string; written: Record<string, any>; deleted: string[]; full: boolean }[] = [];

        chunks[chunkIdx].forEach((node, localIdx) => {
            const nodeRef = doc(nodesCollection, node.id);
            const globalIndex = nodeIndexById?.get(node.id) ?? (chunkIdx * BATCH_SIZE + localIdx);
            // Persist array order so loading restores sibling order correctly
            const local: Record<string, any> = { ...node, nodeIndex: globalIndex };
            const path = nodePath(userId, notebookId, node.id);
            const base = serverFieldSigs.get(path);

            if (!base) {
                // No baseline (brand-new node, or one this tab never loaded) — full write as before.
                const sanitizedNode = cleanNode(local);
                batch.set(nodeRef, sanitizedNode);
                committed.push({ path, written: sanitizedNode, deleted: [], full: true });
                return;
            }

            // Only fields this tab changed since it last saw the server copy; fields it removed
            // (explicit undefined, or simply absent now — e.g. an undo) are deleted for real.
            const written: Record<string, any> = {};
            const deleted: string[] = [];
            for (const [k, v] of Object.entries(local)) {
                if (VIDEO_OWNED_FIELDS.has(k)) continue; // owned by the video tools — see VIDEO_OWNED_FIELDS
                if (v === undefined) { if (base.has(k)) deleted.push(k); continue; }
                const cleaned = cleanNode(v);
                if (base.get(k) !== fieldSig(cleaned)) written[k] = cleaned;
            }
            for (const k of base.keys()) if (!(k in local) && !VIDEO_OWNED_FIELDS.has(k)) deleted.push(k);
            // Title/body go through the compare-and-set path below, never a blind batch write.
            // Text-sync metadata is owned by that path too.
            const textChanged = TEXT_FIELDS.some(k => k in written || deleted.includes(k));
            for (const k of [...TEXT_FIELDS, ...TEXT_META_FIELDS.filter(f => f !== 'updatedAt')]) {
                delete written[k];
                const i = deleted.indexOf(k); if (i >= 0) deleted.splice(i, 1);
            }
            if (textChanged) textJobs.push({ nodeId: node.id, ours: { title: local.title ?? '', content: local.content ?? '' } });
            if (Object.keys(written).length === 0 && deleted.length === 0) { if (!textChanged) skipped++; return; }

            const data: Record<string, any> = { ...written };
            for (const k of deleted) data[k] = deleteField();
            batch.set(nodeRef, data, { mergeFields: Object.keys(data) });
            committed.push({ path, written, deleted, full: false });
        });

        await batch.commit();
        // The server now holds exactly what we wrote — advance the baseline to match.
        for (const c of committed) {
            if (c.full) { recordServerFields(c.path, c.written); continue; }
            const m = serverFieldSigs.get(c.path) ?? new Map<string, number>();
            for (const [k, v] of Object.entries(c.written)) m.set(k, fieldSig(v));
            for (const k of c.deleted) m.delete(k);
            serverFieldSigs.set(c.path, m);
        }
    }

    // Text edits: one compare-and-set transaction per note (normally just the note being typed in).
    for (const job of textJobs) {
        const r = await saveNoteTextCAS(userId, notebookId, job.nodeId, job.ours);
        if (r.outcome !== 'saved') console.warn(`[TEXT SYNC] ${r.outcome} on ${job.nodeId} (rev ${r.textRev})`);
    }

    console.log(`✅ Saved ${nodes.length - skipped} nodes to subcollection (${chunks.length} batches${skipped ? `, ${skipped} unchanged skipped` : ''}${textJobs.length ? `, ${textJobs.length} text CAS` : ''})`);
};

/**
 * Rollback: Delete all nodes from subcollection (used when main doc save fails)
 * This prevents data inconsistency where subcollection has new data but main doc has old data
 */
const deleteNodesFromSubcollection = async (
    userId: string,
    notebookId: string
): Promise<void> => {
    console.warn(`🔄 [ROLLBACK] Deleting nodes from subcollection for notebook ${notebookId}`);

    const nodesCollection = collection(db, "users", userId, "notebooks", notebookId, "nodes");
    const snapshot = await getDocs(nodesCollection);

    if (snapshot.empty) {
        console.log("📭 Subcollection already empty, nothing to rollback");
        return;
    }

    // Delete in batches (Firestore limit: 500 operations/batch)
    const BATCH_SIZE = 500;
    const docs = snapshot.docs;

    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = docs.slice(i, i + BATCH_SIZE);

        chunk.forEach(doc => {
            batch.delete(doc.ref);
        });

        await batch.commit();
    }

    console.log(`✅ [ROLLBACK] Deleted ${docs.length} nodes from subcollection`);
};

/**
 * Data Integrity Verification: Check that subcollection has expected number of nodes
 * Catches data corruption or incomplete saves early
 */
const verifySubcollectionIntegrity = async (
    userId: string,
    notebookId: string,
    expectedNodeCount: number
): Promise<{ valid: boolean; actualCount: number }> => {
    const nodesCollection = collection(db, "users", userId, "notebooks", notebookId, "nodes");
    const snapshot = await getDocs(nodesCollection);
    const actualCount = snapshot.size;

    const valid = actualCount === expectedNodeCount;

    if (!valid) {
        console.error(
            `⚠️ [DATA INTEGRITY] Mismatch for notebook ${notebookId}: ` +
            `Expected ${expectedNodeCount} nodes, found ${actualCount} nodes`
        );
    } else {
        console.log(
            `✅ [DATA INTEGRITY] Verified ${actualCount} nodes in subcollection`
        );
    }

    return { valid, actualCount };
};

export const saveUserData = async (
    userId: string,
    notebooks: Notebook[],
    onProgress?: (msg: string) => void,
    // Per-notebook scoping for the node subcollection write: only nodes in `dirtyIds` get
    // written, only nodes in `deletedIds` get deleted. Omit for a notebook to fall back to
    // writing its full `nodes` array with nothing deleted (used for brand-new/recreated
    // notebooks, where there's no prior baseline to diff against).
    nodeDiffByNotebookId?: Map<string, { dirtyIds: Set<string>; deletedIds: string[] }>
): Promise<{ hasChanges: boolean, data: Notebook[] }> => {
    if (userId === 'guest') {
        if (onProgress) onProgress("Saving locally...");
        localStorage.setItem('guest_data', JSON.stringify(notebooks));
        await new Promise(r => setTimeout(r, 500));
        return { hasChanges: false, data: notebooks };
    }

    console.group("💾 [DEBUG] saveUserData Started");
    try {
        if (onProgress) onProgress("Uploading media...");

        // Check feature flags from localStorage
        const featureFlags = (() => {
            try {
                const saved = localStorage.getItem('ms_feature_flags');
                const parsed = saved ? JSON.parse(saved) : {};
                // FORCE OVERRIDE: Always enable subcollections (ignore stored value)
                return {
                    enableSubcollections: true, // ALWAYS TRUE for production
                    enableCleanup: parsed.enableCleanup ?? true
                };
            } catch {
                return { enableSubcollections: true, enableCleanup: true };
            }
        })();

        const processedNotebooks = await Promise.all(
            notebooks.map(async (nb) => {
                // Step 1: Cleanup transient fields if enabled
                const cleaned = featureFlags.enableCleanup
                    ? cleanupTransientFields(nb)
                    : nb;

                // Step 2: Process for save (upload images, etc.)
                return processNotebookForSave(userId, cleaned);
            })
        );

        if (onProgress) onProgress("Saving to database...");

        const userRef = doc(db, "users", userId);
        await setDoc(userRef, { lastUpdated: Date.now() }, { merge: true });

        const notebooksCollection = collection(userRef, "notebooks");
        const successfullySaved: Notebook[] = [];

        await Promise.all(processedNotebooks.map(async (nb) => {
            try {
                const nbRef = doc(notebooksCollection, nb.id);

                // Phase 2.5: Prepare data for main doc
                // If subcollections enabled, exclude nodes array from main doc
                let nbData;
                let pdfAnnotationsToSave: Notebook['pdfAnnotations'] | undefined;
                try {
                    if (featureFlags.enableSubcollections) {
                        // Create metadata-only version (no nodes, no pdfAnnotations, no internal runtime flags)
                        const { nodes, _isPartiallyLoaded, pdfAnnotations: _pa, ...metadata } = nb;
                        nbData = JSON.parse(JSON.stringify(metadata));
                        pdfAnnotationsToSave = _pa;

                        console.log(
                            `📝 [METADATA ONLY] Saving "${nb.title}" main doc without nodes ` +
                            `(${nodes.length} nodes will be in subcollection only)`
                        );
                    } else {
                        // Legacy mode: Include nodes in main doc
                        nbData = JSON.parse(JSON.stringify(nb));
                    }
                } catch (jsonError) {
                    console.error("Critical: Deep clone failed due to object size", jsonError);
                    throw new Error("ERROR_NOTEBOOK_TOO_LARGE");
                }

                const isNew = nbData._createdLocally;
                delete nbData._createdLocally;

                // Size check (should be much smaller now if subcollections enabled)
                try {
                    const jsonStr = JSON.stringify(nbData);
                    const sizeBytes = getStringByteSize(jsonStr);

                    // DEBUG: Log everything to console to see why size is large
                    console.log(`[DEBUG_SIZE] Saving "${nb.title}"`);
                    console.log(`[DEBUG_SIZE] Flags: ${JSON.stringify(featureFlags)}`);
                    console.log(`[DEBUG_SIZE] Subcollections Enabled: ${featureFlags.enableSubcollections}`);
                    console.log(`[DEBUG_SIZE] Has Nodes in Main Doc: ${!!(nbData as any).nodes}`);
                    console.log(`[DEBUG_SIZE] Main Doc Size: ${(sizeBytes / 1024).toFixed(2)} KB`);

                    if (featureFlags.enableSubcollections) {
                        console.log(`📊 Main doc size for "${nb.title}": ${(sizeBytes / 1024).toFixed(2)} KB`);

                        // Critical: If subcollections enabled but size is large, verify nodes were removed
                        if ((nbData as any).nodes) {
                            console.error("❌ CRITICAL: Nodes were NOT removed from main doc despite flag being true!");
                        }
                    }

                    if (sizeBytes > 950000) {
                        console.error(`❌ ERROR: Notebook too large (${sizeBytes} bytes). Limit: 950000`);
                        throw new Error("ERROR_NOTEBOOK_TOO_LARGE");
                    }
                } catch (sizeError) {
                    console.error("Critical: Size calculation failed", sizeError);
                    throw new Error("ERROR_NOTEBOOK_TOO_LARGE");
                }

                // Phase 2.5: ATOMIC-LIKE SAVE with ROLLBACK capability
                // Save to subcollection FIRST, then main doc
                // If main doc fails → rollback subcollection to prevent inconsistency

                let subcollectionSaved = false;
                let annotationsSaved = false;

                try {
                    // Step 0: Save pdfAnnotations to extra doc (keeps main doc small)
                    if (featureFlags.enableSubcollections && pdfAnnotationsToSave && Object.keys(pdfAnnotationsToSave).length > 0) {
                        try {
                            await saveAnnotationsToExtraDoc(userId, nb.id, pdfAnnotationsToSave);
                            annotationsSaved = true;
                            console.log(`📎 [ANNOTATIONS] Saved pdfAnnotations for "${nb.title}" to extra doc`);
                        } catch (annotErr) {
                            console.error(`Failed to save pdfAnnotations for "${nb.title}" — keeping in main doc:`, annotErr);
                        }
                    }

                    // Step 1: Save to subcollection (if enabled)
                    if (featureFlags.enableSubcollections) {
                        const diff = nodeDiffByNotebookId?.get(nb.id);
                        if (diff) {
                            const nodeIndexById = new Map(nb.nodes.map((n, i) => [n.id, i]));
                            const dirtyNodes = nb.nodes.filter(n => diff.dirtyIds.has(n.id));
                            await saveNodesToSubcollection(userId, nb.id, dirtyNodes, diff.deletedIds, nodeIndexById);
                        } else {
                            await saveNodesToSubcollection(userId, nb.id, nb.nodes);
                        }
                        subcollectionSaved = true;
                    }

                    // Step 2: Save main doc (with or without nodes depending on flag)
                    if (isNew) {
                        await setDoc(nbRef, nbData);
                    } else {
                        // If pdfAnnotations was successfully saved to extra doc, remove from main doc
                        if (featureFlags.enableSubcollections && annotationsSaved) {
                            (nbData as any).pdfAnnotations = deleteField();
                        }
                        await updateDoc(nbRef, nbData);
                    }

                    // Success! Both saves completed
                    successfullySaved.push(nb);

                    // Data integrity check (if subcollections enabled)
                    if (featureFlags.enableSubcollections && subcollectionSaved) {
                        // Verify in background (don't block user)
                        verifySubcollectionIntegrity(userId, nb.id, nb.nodes.length)
                            .catch(err => {
                                console.error(`Failed to verify integrity for "${nb.title}":`, err);
                            });
                    }
                } catch (mainDocError: any) {
                    // SAFETY: Do NOT rollback the subcollection when main doc fails.
                    // Deleting subcollection nodes to "undo" a failed main-doc write is more
                    // dangerous than leaving them: it would destroy any nodes written in this
                    // cycle AND any pre-existing nodes the rollback query happens to read.
                    // Prefer a temporarily stale main-doc (nodeCount/updatedAt mismatch) over
                    // data loss. The next successful save will bring everything back in sync.
                    if (subcollectionSaved) {
                        console.error(
                            `❌ [ATOMIC SAVE] Main doc save failed for "${nb.title}". ` +
                            `Subcollection nodes are preserved — skipping destructive rollback. ` +
                            `Metadata may be temporarily stale until next successful save.`
                        );
                    }

                    // Handle specific error cases
                    if (mainDocError.code === 'not-found') {
                        // Notebook was deleted, create as new
                        nbData.title = `${nbData.title} (Restored Copy)`;
                        await setDoc(nbRef, nbData, { merge: true });

                        // Re-save subcollection if it was rolled back
                        if (subcollectionSaved && featureFlags.enableSubcollections) {
                            await saveNodesToSubcollection(userId, nb.id, nb.nodes);
                        }

                        successfullySaved.push({ ...nb, title: nbData.title });
                        return;
                    }

                    // Re-throw other errors
                    throw mainDocError;
                }
            } catch (e: any) {
                // Trap ANY error that smells like a memory limit or string length issue (RangeError)
                // Outer catch for the entire notebook processing
                if (e instanceof RangeError || e.message?.includes("Invalid string length") || e.message === "ERROR_NOTEBOOK_TOO_LARGE") {
                    throw new Error("ERROR_NOTEBOOK_TOO_LARGE");
                }
                throw e; // Pass other errors to outer catch
            }
        }));

        console.groupEnd();
        return { hasChanges: true, data: successfullySaved };
    } catch (error) {
        console.groupEnd();
        throw error;
    }
};

/**
 * Phase 2.2: Load nodes from subcollection (shadow read mode)
 * Returns nodes array from Firestore subcollection structure
 */
export const loadNotebookNodes = async (
    userId: string,
    notebookId: string
): Promise<MindMapNode[]> => {
    return loadNodesFromSubcollection(userId, notebookId);
};

// ── PDF annotations sync (web ↔ phone) ─────────────────────────────────────────────────────
// The annotations doc is shared by every device. annotBase holds, per notebook, the version this
// tab last saw on the server. A save writes only this tab's changes since then (strokes added /
// removed) on top of the server's current version, inside a transaction — never the whole map
// blindly (that used to wipe highlights made on the phone whenever the web saved the notebook).
const annotBase = new Map<string, Annotations>();
type AnnotListener = (notebookId: string, ours: Annotations, result: Annotations) => void;
const annotListeners = new Set<AnnotListener>();
/** Called when a save had to fold in strokes from another device; the UI should adopt `result`. */
export const onAnnotationsMerged = (fn: AnnotListener) => { annotListeners.add(fn); return () => { annotListeners.delete(fn); }; };
/** Called whenever this tab (re)loads the notebook's annotations from the server. */
export const recordAnnotationBase = (notebookId: string, data: Annotations | undefined) => {
    annotBase.set(notebookId, data ?? {});
};

const saveAnnotationsToExtraDoc = async (
    userId: string,
    notebookId: string,
    pdfAnnotations: Notebook['pdfAnnotations']
): Promise<void> => {
    const ours = (pdfAnnotations ?? {}) as Annotations;
    const base = annotBase.get(notebookId);
    if (base && sameAnnotations(base, ours)) return; // nothing changed here — don't rewrite
    const annotRef = doc(db, "users", userId, "notebooks", notebookId, "extra", "pdfAnnotations");
    const result = await runTransaction(db, async (tx) => {
        const snap = await tx.get(annotRef);
        const theirs = (snap.exists() ? snap.data()?.data : undefined) as Annotations | undefined;
        // Without a known base, only add — never delete strokes this tab may simply not have seen.
        const merged = mergeAnnotations(base ?? {}, ours, theirs ?? {});
        if (!sameAnnotations(merged, theirs)) tx.set(annotRef, { data: merged });
        return merged;
    });
    annotBase.set(notebookId, result);
    if (!sameAnnotations(result, ours)) annotListeners.forEach(fn => { try { fn(notebookId, ours, result); } catch (e) { console.error(e); } });
};

/**
 * Live annotations from other devices for one notebook. `cb` gets the merged version for this
 * tab: the server's strokes plus this tab's unsaved changes (base is advanced to the server's).
 */
export const subscribeToAnnotations = (userId: string, notebookId: string, getLocal: () => Annotations, cb: (merged: Annotations, server: Annotations) => void) => {
    const annotRef = doc(db, "users", userId, "notebooks", notebookId, "extra", "pdfAnnotations");
    return onSnapshot(annotRef, (snap) => {
        if (snap.metadata.hasPendingWrites) return;
        const server = ((snap.exists() ? snap.data()?.data : undefined) ?? {}) as Annotations;
        const base = annotBase.get(notebookId) ?? {};
        if (sameAnnotations(server, base)) return; // nothing new
        const merged = mergeAnnotations(base, getLocal(), server);
        annotBase.set(notebookId, server);
        cb(merged, server);
    }, (err) => console.warn('[ANNOT SYNC] listener error', err));
};

export const loadNotebookAnnotations = async (
    userId: string,
    notebookId: string
): Promise<Notebook['pdfAnnotations'] | undefined> => {
    try {
        const annotRef = doc(db, "users", userId, "notebooks", notebookId, "extra", "pdfAnnotations");
        const snap = await getDoc(annotRef);
        const data = snap.exists() ? (snap.data()?.data as Notebook['pdfAnnotations']) : undefined;
        recordAnnotationBase(notebookId, (data ?? {}) as Annotations);
        if (data) return data;
    } catch (e) {
        console.error(`Failed to load pdfAnnotations for ${notebookId}:`, e);
    }
    return undefined;
};

/** When each notebook's nodes were last read from the server (for the text-change listener). */
export const notebookLoadedAt = new Map<string, number>();

const loadNodesFromSubcollection = async (
    userId: string,
    notebookId: string
): Promise<MindMapNode[]> => {
    const nodesCollection = collection(db, "users", userId, "notebooks", notebookId, "nodes");
    notebookLoadedAt.set(notebookId, Date.now());
    const snapshot = await getDocs(nodesCollection);

    if (snapshot.empty) {
        logToOverlay(`📭 No nodes found in subcollection for notebook ${notebookId}`);
        console.log(`📭 No nodes found in subcollection for notebook ${notebookId}`);
        return [];
    }

    const nodes = snapshot.docs
        .map(doc => {
            // Baseline from the raw server data, before the local sanitizing below — so that a
            // sanitized field (e.g. stale 'generating' → 'none') still counts as changed and saves.
            recordServerFields(nodePath(userId, notebookId, doc.id), doc.data());
            const n = doc.data() as MindMapNode & { nodeIndex?: number };
            // Sanitize stale 'generating' status — it means a background task was
            // interrupted (tab closed, notebook switched) before it could resolve.
            if ((n as any).reviewQuestionStatus === 'generating') {
                (n as any).reviewQuestionStatus = 'none';
            }
            return n;
        })
        .sort((a, b) => (a.nodeIndex ?? 0) - (b.nodeIndex ?? 0));
    console.log(`✅ Loaded ${nodes.length} nodes from subcollection`);
    return nodes;
};

/**
 * Lắng nghe thay đổi dữ liệu người dùng thời gian thực
 * Trả về hàm unsubscribe
 */
export const subscribeToUserData = (
    userId: string,
    callback: (notebooks: Notebook[]) => void,
    getActiveNotebookId?: () => string | null
): (() => void) => {
    if (userId === 'guest') {
        const data = localStorage.getItem('guest_data');
        callback(data ? JSON.parse(data) : []);
        return () => { };
    }

    // Check feature flags
    const featureFlags = (() => {
        try {
            const saved = localStorage.getItem('ms_feature_flags');
            const parsed = saved ? JSON.parse(saved) : {};
            // FORCE OVERRIDE: Always enable subcollections (ignore stored value)
            return {
                enableSubcollections: true, // ALWAYS TRUE for production
                enableCleanup: parsed.enableCleanup ?? true
            };
        } catch {
            return { enableSubcollections: true, enableCleanup: true };
        }
    })();

    const notebooksCollection = collection(db, "users", userId, "notebooks");
    const q = query(notebooksCollection, orderBy("updatedAt", "desc"));

    return onSnapshot(q, async (snapshot) => {
        const notebooks = snapshot.docs.map(doc => {
            const data = doc.data() as Notebook;
            // Phase 2.5: Ensure nodes array exists (fallback to empty if main doc cleaned up)
            return { ...data, nodes: data.nodes || [] };
        });

        // Phase 2.3: Primary read from subcollection (if enabled)
        if (featureFlags.enableSubcollections) {
            console.group("📖 [PRIMARY READ - REALTIME] Loading from subcollection");
            logToOverlay("Syncing... (Subcollections Enabled)");

            // Determine active notebook — only load its subcollection eagerly.
            // All other notebooks get _isPartiallyLoaded: true and are loaded on demand.
            // This reduces startup reads from N×M to 1 subcollection read and eliminates
            // the risk of a failed parallel read being silently treated as "notebook is empty".
            const _urlId = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('id') : null;
            const _savedId = typeof window !== 'undefined' ? localStorage.getItem('sb_active_notebook') : null;
            const _activeNbs = notebooks.filter(nb => !nb.isDeleted);
            const _targetNb = _urlId ? _activeNbs.find(nb => nb.id === _urlId)
                : _savedId ? _activeNbs.find(nb => nb.id === _savedId) : null;
            const activeId = getActiveNotebookId?.() ?? _targetNb?.id ?? _activeNbs[0]?.id ?? null;

            try {
                const enrichedNotebooks = await Promise.all(
                    notebooks.map(async (notebook) => {
                        // Non-active notebooks: return as partially loaded — lazy-load on demand
                        if (notebook.id !== activeId) {
                            return { ...notebook, nodes: [], _isPartiallyLoaded: true as const };
                        }

                        // Active notebook: load subcollection now
                        try {
                            const needExtraAnnotations = !notebook.pdfAnnotations || Object.keys(notebook.pdfAnnotations).length === 0;
                            const [subcollectionNodes, extraAnnotations] = await Promise.all([
                                loadNodesFromSubcollection(userId, notebook.id),
                                needExtraAnnotations ? loadNotebookAnnotations(userId, notebook.id) : Promise.resolve(undefined),
                            ]);

                            if (subcollectionNodes.length > 0) {
                                logToOverlay(`✅ Using subcollection for "${notebook.title}": ${subcollectionNodes.length} nodes`);
                                console.log(`✅ Using subcollection for "${notebook.title}": ${subcollectionNodes.length} nodes`);
                                return {
                                    ...notebook,
                                    nodes: subcollectionNodes,
                                    ...(extraAnnotations ? { pdfAnnotations: extraAnnotations } : {}),
                                };
                            }

                            // Subcollection empty — always suspicious for an existing Firestore notebook.
                            // nodeCount may be stale (0) even when notes exist, so we can't trust it.
                            // Show retry overlay; if retry also returns 0, _loadFailed is cleared and
                            // normal empty-notebook onboarding is shown.
                            const storedCount = notebook.nodeCount ?? 0;
                            logToOverlay(`🚨 [LOAD FAILED] "${notebook.title}": subcollection empty (nodeCount=${storedCount}) — showing retry overlay`);
                            console.error(`🚨 [LOAD FAILED] "${notebook.title}": subcollection returned empty — marking _loadFailed`);
                            return { ...notebook, nodes: [], _loadFailed: true as const };
                        } catch (error: any) {
                            logToOverlay(`❌ Failed to load subcollection for "${notebook.title}": ${error.message}`);
                            console.error(`❌ [LOAD FAILED] "${notebook.title}": subcollection read threw — marking _loadFailed`, error);
                            return { ...notebook, nodes: [], _loadFailed: true as const };
                        }
                    })
                );

                console.groupEnd();
                callback(enrichedNotebooks);
            } catch (error) {
                console.error("❌ Error enriching notebooks from subcollection:", error);
                console.groupEnd();
                callback(notebooks); // Fallback to main doc
            }
        } else {
            // Legacy mode: Use main document data
            callback(notebooks);
        }
    }, (error) => {
        console.error("Firestore subscription error:", error);
    });
};

export const loadUserData = async (userId: string): Promise<Notebook[]> => {
    if (userId === 'guest') {
        const data = localStorage.getItem('guest_data');
        return data ? JSON.parse(data) : [];
    }

    // Check feature flags
    const featureFlags = (() => {
        try {
            const saved = localStorage.getItem('ms_feature_flags');
            const parsed = saved ? JSON.parse(saved) : {};
            // FORCE OVERRIDE: Always enable subcollections (ignore stored value)
            return {
                enableSubcollections: true, // ALWAYS TRUE for production
                enableCleanup: parsed.enableCleanup ?? true
            };
        } catch {
            return { enableSubcollections: true, enableCleanup: true };
        }
    })();

    try {
        const notebooksCollection = collection(db, "users", userId, "notebooks");
        const q = query(notebooksCollection, orderBy("updatedAt", "desc"));
        const snapshot = await getDocs(q);

        if (snapshot.empty) return [];

        const notebooks = snapshot.docs.map(doc => {
            const data = doc.data() as Notebook;
            // Phase 2.5: Ensure nodes array exists (fallback to empty if main doc cleaned up)
            return { ...data, nodes: data.nodes || [] };
        });

        // Phase 2.3: Primary read from subcollection (if enabled)
        if (featureFlags.enableSubcollections) {
            console.group("📖 [PRIMARY READ] Loading from subcollection");

            const enrichedNotebooks = await Promise.all(
                notebooks.map(async (notebook) => {
                    try {
                        const subcollectionNodes = await loadNodesFromSubcollection(userId, notebook.id);

                        // If subcollection has nodes, use it as primary source
                        if (subcollectionNodes.length > 0) {
                            console.log(
                                `✅ Using subcollection for "${notebook.title}": ${subcollectionNodes.length} nodes`
                            );

                            // Merge: Use subcollection nodes + main doc metadata
                            return {
                                ...notebook,
                                nodes: subcollectionNodes
                            };
                        } else {
                            // Fallback: Subcollection empty, use main doc
                            const mainDocNodeCount = (notebook.nodes || []).length;
                            console.warn(
                                `⚠️ Fallback to main doc for "${notebook.title}": ` +
                                `subcollection empty, using ${mainDocNodeCount} nodes from main doc`
                            );
                            return { ...notebook, nodes: notebook.nodes || [] };
                        }
                    } catch (error) {
                        console.error(`❌ Failed to load subcollection for "${notebook.title}":`, error);
                        const mainDocNodeCount = (notebook.nodes || []).length;
                        console.warn(`⚠️ Fallback to main doc: ${mainDocNodeCount} nodes`);
                        return { ...notebook, nodes: notebook.nodes || [] };
                    }
                })
            );

            console.groupEnd();
            return enrichedNotebooks;
        }

        // Phase 2.2 fallback: Return main document data (legacy mode)
        return notebooks;
    } catch (error) {
        console.error("Load failed", error);
        throw error;
    }
};

export const loadSharedNotebook = async (userId: string, notebookId: string): Promise<Notebook | null | 'private'> => {
    console.log(`[DEBUG_SHARE] loadSharedNotebook START: userId=${userId}, notebookId=${notebookId}`);
    try {
        const docRef = doc(db, "users", userId, "notebooks", notebookId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            // docSnap.data() does not include the document ID — use docSnap.id as authoritative
            const data = { ...docSnap.data(), id: docSnap.id } as Notebook;

            // Always prefer subcollection nodes (same as owner's loadUserData flow)
            try {
                const subcollectionNodes = await loadNodesFromSubcollection(userId, notebookId);
                if (subcollectionNodes.length > 0) {
                    data.nodes = subcollectionNodes;
                }
            } catch (e) {
                console.error("[DEBUG_SHARE] Failed to load nodes from subcollection", e);
                // Fall back to main doc nodes already in data.nodes
            }

            console.log(`[DEBUG_SHARE] loadSharedNotebook SUCCESS: Found notebook ID=${data.id}, nodes=${data.nodes?.length || 0}`);
            return data;
        } else {
            console.warn(`[DEBUG_SHARE] loadSharedNotebook FAIL: Document does not exist at path.`);
            return null;
        }
    } catch (error: any) {
        if (error.code === 'permission-denied') {
            console.warn("[DEBUG_SHARE] loadSharedNotebook: permission-denied (Notebook is private)");
            return 'private';
        } else {
            console.error("[DEBUG_SHARE] loadSharedNotebook: Unexpected Error", error);
        }
        return null;
    }
};

export const deleteNotebookData = async (userId: string, notebookId: string): Promise<void> => {
    if (userId === 'guest') {
        const data = localStorage.getItem('guest_data');
        if (data) {
            const notebooks = JSON.parse(data) as Notebook[];
            const updated = notebooks.filter(nb => nb.id !== notebookId);
            localStorage.setItem('guest_data', JSON.stringify(updated));
        }
        return;
    }

    try {
        const deleteNotebook = httpsCallable(functions, 'deleteNotebook');
        await deleteNotebook({ notebookId });
    } catch (error) {
        console.error("Delete failed via Cloud Function:", error);
        throw error;
    }
};

export const restoreNotebookData = async (notebookId: string): Promise<void> => {
    const restoreNotebook = httpsCallable(functions, 'restoreNotebook');
    await restoreNotebook({ notebookId });
};

export const permanentlyDeleteNotebookData = async (notebookId: string): Promise<void> => {
    const permanentlyDeleteNotebook = httpsCallable(functions, 'permanentlyDeleteNotebook');
    await permanentlyDeleteNotebook({ notebookId });
};

export const setNotebookPublic = async (userId: string, notebookId: string, isPublic: boolean, notebookMeta?: { title: string; nodeCount: number; ownerName?: string }): Promise<void> => {
    try {
        const nbRef = doc(db, "users", userId, "notebooks", notebookId);
        await updateDoc(nbRef, { isPublic });

        // Mirror to public_notebooks collection for marketplace queries
        const publicRef = doc(db, "public_notebooks", notebookId);
        if (isPublic && notebookMeta) {
            await setDoc(publicRef, {
                notebookId,
                ownerId: userId,
                ownerName: notebookMeta.ownerName || "Anonymous",
                title: notebookMeta.title,
                nodeCount: notebookMeta.nodeCount,
                stars: 0,
                clones: 0,
                updatedAt: Date.now(),
                createdAt: Date.now(),
            }, { merge: true });
        } else if (!isPublic) {
            const { deleteDoc } = await import("firebase/firestore");
            await deleteDoc(publicRef).catch(() => {});
        }
    } catch (error) {
        console.error("Failed to update public status", error);
        throw error;
    }
};
export const updatePublicNotebookTitle = async (notebookId: string, newTitle: string): Promise<void> => {
    try {
        const publicRef = doc(db, "public_notebooks", notebookId);
        await updateDoc(publicRef, { title: newTitle, updatedAt: Date.now() });
    } catch (error) {
        // Silently ignore — public doc may not exist if notebook was never shared
        console.warn("updatePublicNotebookTitle: could not update", error);
    }
};

export const saveNotebookPatternsEnabled = async (
    userId: string,
    notebookId: string,
    enabled: boolean
): Promise<void> => {
    if (userId === 'guest') return;
    try {
        const nbRef = doc(db, "users", userId, "notebooks", notebookId);
        await updateDoc(nbRef, { patternsEnabled: enabled });
    } catch (error) {
        console.error("Failed to save notebook patterns setting", error);
        throw error;
    }
};

export const saveNotebookAISettings = async (
    userId: string,
    notebookId: string,
    settings: AIBehaviorSettings | null
): Promise<void> => {
    if (userId === 'guest') return;
    try {
        const nbRef = doc(db, "users", userId, "notebooks", notebookId);
        if (settings === null) {
            await updateDoc(nbRef, { aiSettings: deleteField() });
        } else {
            await updateDoc(nbRef, { aiSettings: JSON.parse(JSON.stringify(settings)) });
        }
    } catch (error) {
        console.error("Failed to save notebook AI settings", error);
        throw error;
    }
};

export const saveNotebookGroup = async (
    userId: string,
    notebookId: string,
    githubGroup: string
): Promise<void> => {
    if (userId === 'guest') return;
    try {
        const nbRef = doc(db, "users", userId, "notebooks", notebookId);
        await updateDoc(nbRef, { githubGroup });
    } catch (error) {
        console.error("Failed to save notebook group", error);
        throw error;
    }
};

export const saveNotebookDescription = async (
    userId: string,
    notebookId: string,
    description: string
): Promise<void> => {
    if (userId === 'guest') return;
    try {
        const nbRef = doc(db, "users", userId, "notebooks", notebookId);
        await updateDoc(nbRef, { description });
    } catch (error) {
        console.error("Failed to save notebook description", error);
        throw error;
    }
};

export const saveUserProfile = async (userId: string, profile: { email: string; name: string; photoURL?: string | null }): Promise<void> => {
    if (userId === 'guest') return;
    try {
        const userRef = doc(db, 'users', userId);
        await setDoc(userRef, {
            email: profile.email,
            name: profile.name,
            photoURL: profile.photoURL || null,
            lastSeen: Date.now(),
        }, { merge: true });
    } catch (e) {
        console.error('saveUserProfile failed:', e);
    }
};

// --- USER PREFS (cross-device sync) ---

export interface UserPrefs {
    language?: 'vietnamese' | 'english';
    darkMode?: boolean;
}

export const saveUserPrefs = async (userId: string, prefs: Partial<UserPrefs>): Promise<void> => {
    if (!userId || userId === 'guest') return;
    try {
        await setDoc(doc(db, 'users', userId), { prefs }, { merge: true });
    } catch (e) {
        console.error('saveUserPrefs failed:', e);
    }
};

export const loadUserPrefs = async (userId: string): Promise<UserPrefs | null> => {
    if (!userId || userId === 'guest') return null;
    try {
        const snap = await getDoc(doc(db, 'users', userId));
        return snap.exists() ? (snap.data()?.prefs ?? null) : null;
    } catch (e) {
        console.error('loadUserPrefs failed:', e);
        return null;
    }
};

// --- MARKETPLACE ---

export interface PublicNotebookMeta {
    notebookId: string;
    ownerId: string;
    ownerName: string;
    title: string;
    nodeCount: number;
    stars: number;
    clones: number;
    updatedAt: number;
    createdAt: number;
}

export const fetchPublicNotebooks = async (limitCount = 20): Promise<PublicNotebookMeta[]> => {
    try {
        const q = query(collection(db, "public_notebooks"), orderBy("clones", "desc"), limit(limitCount));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => d.data() as PublicNotebookMeta);
    } catch (error) {
        console.error("Failed to fetch public notebooks", error);
        return [];
    }
};

export const removeStalePublicNotebook = async (notebookId: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, "public_notebooks", notebookId));
    } catch {
        // Silently ignore — user may not have permission if not the owner
    }
};

// Returns true if notebook exists AND isPublic=true; false if deleted or private
export const checkNotebookStillPublic = async (ownerId: string, notebookId: string): Promise<boolean> => {
    try {
        const snap = await getDoc(doc(db, "users", ownerId, "notebooks", notebookId));
        return snap.exists() && snap.data()?.isPublic === true;
    } catch {
        return false;
    }
};

export const clonePublicNotebook = async (
    sourceOwnerId: string,
    sourceNotebookId: string,
    targetUserId: string,
    clearContent: boolean
): Promise<string> => {
    // Server-side clone via Cloud Function
    // Fixes: data corruption on bad network, broken clone counter, race conditions
    const cloneNotebookFn = httpsCallable(functions, 'cloneNotebook');
    const result = await cloneNotebookFn({
        sourceOwnerId,
        sourceNotebookId,
        clearContent,
    });
    const data = result.data as { success: boolean; newNotebookId: string; nodeCount: number };
    return data.newNotebookId;
};

// Test-only access to the node-subcollection save/load pair (see tests/nodeFieldSave.test.ts).
export const __nodeSaveTestHooks = { saveNodesToSubcollection, loadNodesFromSubcollection, saveNoteTextCAS, saveAnnotationsToExtraDoc };
